import { Children, cloneElement, isValidElement, ReactElement, ReactNode } from "react";
import { Helmet as ReactHelmet, HelmetProps } from "react-helmet-async";

/**
 * Drop-in replacement for react-helmet-async's <Helmet>. Always import
 * Helmet from this file, not from react-helmet-async.
 *
 * React 19 requires the child of a <title> element to be a single string,
 * and react-helmet-async@3 (in its React 19 mode) passes title children
 * through to a natively-rendered, React-hoisted <title> as-is. JSX like:
 *
 *   <title>Log in{titleTagSuffix}</title>
 *
 * produces an array of children, which React 19 renders as an empty
 * document title. This wrapper joins <title> children into a single
 * string before handing them to react-helmet-async.
 */
const Helmet = ({ children, ...props }: HelmetProps & { children?: ReactNode }) => (
  <ReactHelmet {...props}>
    {Children.map(children, (child) => {
      if (isValidElement(child) && child.type === "title") {
        const el = child as ReactElement<{ children?: ReactNode }>;
        return cloneElement(el, {}, Children.toArray(el.props.children).join(""));
      }
      return child;
    })}
  </ReactHelmet>
);

export { Helmet };
export default Helmet;
